'use client';

import { useCallback, useEffect, useState } from 'react';

// A CTA passing under the pinned header is already hidden even though it is
// still on screen. The header is measured directly: `--header-height` keeps the
// expanded height after the header shrinks, and `--alert-banner-height` can stay
// set once the banner is gone, so the variables overstate what is covered.
function readTopInset(): number {
  let inset = 0;
  for (const header of document.querySelectorAll('header')) {
    const { position } = getComputedStyle(header);
    if (position !== 'fixed' && position !== 'sticky') continue;
    const { top, bottom } = header.getBoundingClientRect();
    if (bottom > 0 && top < window.innerHeight / 2) inset = Math.max(inset, bottom);
  }
  return Math.round(inset);
}

/**
 * Reports whether the observed CTA has scrolled fully above the visible area
 * below the fixed header. A CTA still below the fold does not count, so the bar
 * only appears once the user has actually passed the on-page purchase area.
 */
export function useCtaOffscreen<T extends Element>(enabled: boolean) {
  const [node, setNode] = useState<T | null>(null);
  const [isOffscreen, setIsOffscreen] = useState(false);
  const [topInset, setTopInset] = useState(0);

  // State-backed callback ref so the observer re-attaches when the CTA remounts.
  const ref = useCallback((element: T | null) => setNode(element), []);

  useEffect(() => {
    if (!enabled) return;

    const sync = () => setTopInset(readTopInset());
    sync();

    // The header switches position and size by class on scroll, and slides in/out
    // with transitions that only settle on transitionend.
    const headers = [...document.querySelectorAll('header')];
    const mutationObserver = new MutationObserver(sync);
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync);
    for (const header of headers) {
      mutationObserver.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
      resizeObserver?.observe(header);
      header.addEventListener('transitionend', sync);
    }
    window.addEventListener('resize', sync);

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      for (const header of headers) header.removeEventListener('transitionend', sync);
      window.removeEventListener('resize', sync);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !node || typeof IntersectionObserver === 'undefined') {
      setIsOffscreen(false);
      return;
    }

    // The root is stretched far below the fold so the only edge that can flip the
    // intersection is the top edge; a plain viewport root never fires when a
    // jump (anchor, scroll restoration) moves the CTA from below to above.
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsOffscreen(!entry.isIntersecting && entry.boundingClientRect.top < topInset);
      },
      { rootMargin: `${-topInset}px 0px 100000px 0px`, threshold: 0 }
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [enabled, node, topInset]);

  return { ref, element: node, isOffscreen };
}
